import { useMemo, useState, type FormEvent } from 'react'
import { AnalyticsConsentCard } from './analytics/AnalyticsConsent'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import { cartBoxCount } from './order/cartStorage'
import './styles/support.css'

type SupportCategory = 'Order' | 'Delivery' | 'Payment' | 'Product' | 'Menu' | 'Allergen' | 'Website' | 'General' | 'Other'
const CATEGORIES: SupportCategory[] = ['Order', 'Delivery', 'Payment', 'Product', 'Menu', 'Allergen', 'Website', 'General', 'Other']

type IconName = 'order' | 'delivery' | 'payment' | 'product' | 'menu' | 'allergen' | 'website' | 'general' | 'other' | 'mail' | 'search' | 'ticket' | 'lock' | 'arrow'

const CATEGORY_ICONS: Record<SupportCategory, IconName> = {
  Order: 'order',
  Delivery: 'delivery',
  Payment: 'payment',
  Product: 'product',
  Menu: 'menu',
  Allergen: 'allergen',
  Website: 'website',
  General: 'general',
  Other: 'other',
}

type Draft = {
  category: SupportCategory
  name: string
  email: string
  phone: string
  subject: string
  orderReference: string
  message: string
  companyWebsite: string
}

function initialDraft(): Draft {
  return {
    category: window.location.pathname === '/contact' ? 'General' : 'Order',
    name: '', email: '', phone: '', subject: '', orderReference: '', message: '', companyWebsite: '',
  }
}

function SupportIcon({ name }: { name: IconName }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  }

  if (name === 'order') return <svg {...common}><path d="M6.5 8.5h11l1 11h-13l1-11Z" /><path d="M9 9V6.8a3 3 0 0 1 6 0V9" /></svg>
  if (name === 'delivery') return <svg {...common}><path d="M3 7h11v10H3z" /><path d="M14 10h4l3 3v4h-7" /><circle cx="7" cy="18" r="1.7" /><circle cx="17.5" cy="18" r="1.7" /></svg>
  if (name === 'payment') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18M7 14h4" /></svg>
  if (name === 'product') return <svg {...common}><path d="m12 3 8 4.4v9.2L12 21l-8-4.4V7.4L12 3Z" /><path d="m4.4 7.6 7.6 4.2 7.6-4.2M12 11.8V21" /></svg>
  if (name === 'menu') return <svg {...common}><path d="M6 3v7M3.5 3v4.5A2.5 2.5 0 0 0 6 10a2.5 2.5 0 0 0 2.5-2.5V3M6 10v11" /><path d="M16 3v18M16 3c3 1.5 4.5 4 4.5 7H16" /></svg>
  if (name === 'allergen') return <svg {...common}><path d="M19.5 4.5C13 4.6 7.4 7.3 5.2 13.1c-1 2.7.7 5.4 3.5 5.4 5.7 0 9-5.1 10.8-14Z" /><path d="M5 20c2.5-4.7 6-8 10.8-10.8" /></svg>
  if (name === 'website') return <svg {...common}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
  if (name === 'general') return <svg {...common}><path d="M21 11.5a7.5 7.5 0 0 1-8 7.5 9 9 0 0 1-3.5-.7L4 21l1.4-4.4A7.2 7.2 0 0 1 3 11.5C3 7.4 7 4 12 4s9 3.4 9 7.5Z" /></svg>
  if (name === 'other') return <svg {...common}><circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" /></svg>
  if (name === 'mail') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
  if (name === 'search') return <svg {...common}><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" /></svg>
  if (name === 'ticket') return <svg {...common}><path d="M4 8.5 8.5 4l2 2a2.1 2.1 0 0 0 3 0l2-2L20 8.5l-2 2a2.1 2.1 0 0 0 0 3l2 2-4.5 4.5-2-2a2.1 2.1 0 0 0-3 0l-2 2L4 15.5l2-2a2.1 2.1 0 0 0 0-3l-2-2Z" /><path d="m10 10 4 4" /></svg>
  if (name === 'lock') return <svg {...common}><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
  return <svg {...common}><path d="M5 12h14M14 7l5 5-5 5" /></svg>
}

function WhatHappensNext() {
  return (
    <aside className="support-next" aria-labelledby="support-next-title">
      <div className="support-next__heading">
        <h2 id="support-next-title">What happens next?</h2>
        <p>We&apos;ll keep you in the loop from here.</p>
      </div>

      <ol className="support-next__steps">
        <li>
          <span className="support-next__number">1</span>
          <span className="support-next__icon"><SupportIcon name="mail" /></span>
          <div><strong>We receive your request</strong><p>Your request goes straight to our team and we&apos;ll confirm we&apos;ve got it.</p></div>
        </li>
        <li>
          <span className="support-next__number">2</span>
          <span className="support-next__icon"><SupportIcon name="search" /></span>
          <div><strong>We look into it</strong><p>Our team will check the details and get everything we need.</p></div>
        </li>
        <li>
          <span className="support-next__number">3</span>
          <span className="support-next__icon"><SupportIcon name="mail" /></span>
          <div><strong>We get back to you</strong><p>You&apos;ll receive a reply by email, along with your support reference number.</p></div>
        </li>
      </ol>

      <div className="support-reference-example">
        <div className="support-reference-example__top">
          <span className="support-reference-example__icon"><SupportIcon name="ticket" /></span>
          <div>
            <strong>Your support reference</strong>
            <p>Successful submissions will receive a support reference number, for example:</p>
          </div>
        </div>
        <div className="support-reference-example__code">SGT-0001</div>
        <p className="support-reference-example__note">Keep this number handy when contacting us about the same issue.</p>
      </div>
    </aside>
  )
}

export default function SupportPage() {
  const [draft, setDraft] = useState<Draft>(() => initialDraft())
  const [submissionKey] = useState(() => crypto.randomUUID())
  const [formRenderedAt] = useState(() => Date.now())
  const [formStartedAt, setFormStartedAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [reference, setReference] = useState('')
  const cartCount = useMemo(() => cartBoxCount(), [])

  function markStarted() {
    setFormStartedAt((current) => current ?? Date.now())
  }

  function field<K extends keyof Draft>(key: K, value: Draft[K]) {
    markStarted()
    setDraft((current) => ({ ...current, [key]: value }))
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const response = await fetch('/api/support/tickets', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, submissionKey, formRenderedAt, formStartedAt }),
      })
      const payload = await response.json().catch(() => null) as { ticket?: { reference?: string }; message?: string } | null
      if (!response.ok || !payload?.ticket?.reference) throw new Error(payload?.message || 'Your support request could not be sent.')
      setReference(payload.ticket.reference)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Your support request could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sg-v3 support-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref="/support" />
      <main id="main-content" tabIndex={-1}>
        <section className="support-body">
          <div className="sg-v3-shell support-body__inner">
            <header className="support-hero">
              <span className="support-eyebrow">SUPPORT</span>
              <h1><span>Need a </span><strong>hand?</strong></h1>
              <p>Tell us what happened and our Sugar Gremlin team will look into it.<br />We&apos;re real people, we care, and we&apos;ll get back to you as soon as we can.</p>
            </header>

            <section className="support-categories" aria-labelledby="support-category-title">
              <h2 id="support-category-title">What&apos;s this about?</h2>
              <div className="support-category-grid" role="radiogroup" aria-label="Support category">
                {CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    role="radio"
                    aria-checked={draft.category === category}
                    className={draft.category === category ? 'is-selected' : ''}
                    onClick={() => field('category', category)}
                  >
                    <span className="support-category-grid__icon"><SupportIcon name={CATEGORY_ICONS[category]} /></span>
                    <strong>{category}</strong>
                  </button>
                ))}
              </div>
              <label className="support-category-select">
                <span>Support category</span>
                <select
                  value={draft.category}
                  onChange={(event) => field('category', event.target.value as SupportCategory)}
                >
                  {CATEGORIES.map((category) => <option value={category} key={category}>{category}</option>)}
                </select>
              </label>
            </section>

            <div className="support-main-grid">
              <section className="support-request-card" aria-labelledby="support-form-title">
                {reference ? (
                  <div className="support-success" role="status" aria-live="polite">
                    <span className="support-eyebrow">REQUEST RECEIVED</span>
                    <h2 id="support-form-title">Thanks — we&apos;ve got it.</h2>
                    <p>Your support reference is:</p>
                    <strong className="support-success__reference">{reference}</strong>
                    <p>You can reply to the acknowledgement email if you need to add anything else.</p>
                    <a href="/">Back to home <SupportIcon name="arrow" /></a>
                  </div>
                ) : (
                  <>
                    <div className="support-card-heading">
                      <h2 id="support-form-title">Send a support request</h2>
                      <p>Fill in the details below and we&apos;ll take it from here.</p>
                    </div>

                    <form className="support-form" onSubmit={submit} onFocusCapture={markStarted} aria-busy={busy}>
                      <div className="support-form__grid">
                        <label>
                          <span>Full name <b aria-hidden="true">*</b></span>
                          <input value={draft.name} onChange={(event) => field('name', event.target.value)} maxLength={120} required autoComplete="name" placeholder="Jane Smith" />
                        </label>
                        <label>
                          <span>Email address <b aria-hidden="true">*</b></span>
                          <input value={draft.email} onChange={(event) => field('email', event.target.value)} maxLength={254} required type="email" autoComplete="email" placeholder="you@example.com" />
                        </label>
                        <label>
                          <span>Phone number <em>(optional)</em></span>
                          <input value={draft.phone} onChange={(event) => field('phone', event.target.value)} maxLength={80} autoComplete="tel" placeholder="021 123 4567" />
                        </label>
                        <label>
                          <span>Order number <em>(optional)</em></span>
                          <input value={draft.orderReference} onChange={(event) => field('orderReference', event.target.value.toUpperCase())} maxLength={20} placeholder="e.g. SG123" />
                        </label>
                        <label className="support-form__wide">
                          <span>Subject <b aria-hidden="true">*</b></span>
                          <input value={draft.subject} onChange={(event) => field('subject', event.target.value)} maxLength={180} required placeholder="A short summary of your request" />
                        </label>
                        <label className="support-form__wide support-message-field">
                          <span>What happened? <b aria-hidden="true">*</b></span>
                          <span className="support-message-field__control">
                            <textarea value={draft.message} onChange={(event) => field('message', event.target.value)} maxLength={10000} rows={8} required placeholder="Tell us what happened, including any relevant details..." />
                            <small>{draft.message.length} / 10000</small>
                          </span>
                        </label>
                        <label className="support-honeypot" aria-hidden="true">
                          <span>Company website</span>
                          <input value={draft.companyWebsite} onChange={(event) => field('companyWebsite', event.target.value)} tabIndex={-1} autoComplete="off" />
                        </label>
                      </div>

                      {error ? <div className="support-form__error" role="alert">{error}</div> : null}

                      <button className="support-submit" type="submit" disabled={busy}>
                        <span>{busy ? 'Sending…' : 'Send support request'}</span>
                        {!busy ? <SupportIcon name="arrow" /> : null}
                      </button>

                      <div className="support-privacy">
                        <span className="support-privacy__icon"><SupportIcon name="lock" /></span>
                        <div>
                          <strong>For your security and privacy</strong>
                          <p>Please don&apos;t send payment-card images, identity documents, medical information or unrelated personal documents through this form.</p>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </section>

              <WhatHappensNext />
            </div>

            <section className="support-faq" aria-labelledby="support-faq-title">
              <span className="support-faq__icon"><SupportIcon name="general" /></span>
              <div className="support-faq__copy">
                <span className="support-eyebrow">STILL NEED HELP?</span>
                <h2 id="support-faq-title">Check out our <strong>FAQs</strong></h2>
                <p>You might find a quick answer in our frequently asked questions.</p>
              </div>
              <a className="support-faq__button" href="/faq">View FAQs <SupportIcon name="arrow" /></a>
              <p className="support-faq__note">Real people.<br />Sweet support. <span aria-hidden="true">♡</span></p>
            </section>
          </div>
        </section>
      </main>
      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}
